import { useAtom, useAtomValue } from 'jotai';
import Stack from '@mui/material/Stack';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import * as state from '@/components/Analysis/state';
import { EquipmentLineBreak, EquipmentsContainer } from '../styles';
import {
  ReportInternalType,
  TemplateTypeEnum,
  useAllAssemblyTemplatesForFilterSideBarQuery,
  useGetAssemblyDetailsQuery,
} from '@/__generated__/graphql';
import { useEquipmentList, usePointOfInterestDetails } from '../../hooks';
import { ReportSections } from '@/components/Analysis/ReportsRevamped/ReportSections';
import { useEffect } from 'react';
import { useGetStructureId } from '@/hooks/useGetStructureId';

type EquipmentsListProps = {
  reportType: ReportInternalType;
};

export const EquipmentsList = ({ reportType }: EquipmentsListProps) => {
  const [assemblyTemplate, setAssemblyTemplate] = useAtom(state.assemblyTemplate);
  const selectedReport = useAtomValue(state.selectedReport);
  const equipmentList = useEquipmentList(selectedReport);
  const { groupPoiByEquipmentWithNumbers: groupedPOIs } = usePointOfInterestDetails();
  const navbarHeight = useNavbarHeight();
  const structureId = useGetStructureId();
  const orderOfReport = selectedReport?.reportOrder;

  const { data: templatesData } = useAllAssemblyTemplatesForFilterSideBarQuery({
    variables: {
      structureId,
      type: TemplateTypeEnum.Assembly,
    },
    skip: !structureId || !!assemblyTemplate,
  });

  const { data: assemblyDetails } = useGetAssemblyDetailsQuery({
    variables: {
      assemblyId: selectedReport?.componentIds?.at(0), // using first component id for now
    },
    skip: !selectedReport?.componentIds || !structureId,
  });

  useEffect(() => {
    const assemblyTemplates = templatesData?.allTemplates;
    if (assemblyTemplates) {
      setAssemblyTemplate(assemblyTemplates[0]);
    }
  }, [templatesData?.allTemplates, setAssemblyTemplate]);

  return (
    <EquipmentsContainer navbarHeight={navbarHeight}>
      <Stack marginBottom={8}>
        {assemblyDetails?.assembly &&
          equipmentList.length === 0 &&
          selectedReport?.componentIds && (
            <ReportSections
              pointsOfInterests={[]}
              reportType={reportType}
              componentId={selectedReport.componentIds?.at(0) ?? undefined}
              equipment={assemblyDetails?.assembly}
            />
          )}
        {equipmentList.map((equipment, index) => {
          if (!equipment) return null;
          const assemblyId = equipment.id;
          const assemblyName = equipment.tagName;
          const orderedPoiIds = orderOfReport?.poiOrder[assemblyId] || [];
          const orderedPois =
            assemblyId && orderedPoiIds.length > 0
              ? orderedPoiIds.map((id: string) =>
                  groupedPOIs[assemblyName].find((poi) => poi.id === id)
                )
              : groupedPOIs[assemblyName] || [];
          return (
            <>
              <ReportSections
                key={equipment.id}
                equipment={equipment}
                pointsOfInterests={orderedPois}
                reportType={reportType}
              />
              {index !== equipmentList.length - 1 && <EquipmentLineBreak />}
            </>
          );
        })}
      </Stack>
    </EquipmentsContainer>
  );
};
